import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { axiosInstance } from './config';
import { useState, useEffect } from 'react';

export const sessionResponsesApi = {
  // Get all competencies selected for a session
  getSessionCompetenciesIDs: async (sessionId) => {
    const response = await axiosInstance.get(`/sessions/${sessionId}/responses`);
    // Extract competency IDs from the response
    return response.data.map(item => item.competency_id);
  },

  // Get detailed session responses
  getSessionResponses: async (sessionId) => {
    const response = await axiosInstance.get(`/sessions/${sessionId}/responses`);
    return response.data;
  },

  // Save all selected competencies at once (bulk operation)
  bulkSaveSessionResponses: async ({ sessionId, competencyIds }) => {
    console.log(`Saving ${competencyIds.length} competencies for session ${sessionId}`);
    const response = await axiosInstance.post(`/sessions/${sessionId}/responses/bulk`, {
      competencyIds
    });
    console.log(`Saved session responses:`, response.data);
    return response.data;
  },

  createOrUpdateSessionResponse: async ({ sessionId, competencyId, status = 'selected' }) => {
    console.log(`Adding competency ${competencyId} to session ${sessionId}`);
    const response = await axiosInstance.put(`/sessions/${sessionId}/competency/${competencyId}`, {
      status
    });
    console.log(`Added/updated session response:`, response.data);
    return response.data;
  },

  removeCompetencyFromSession: async ({ sessionId, competencyId }) => {
    console.log(`Removing competency ${competencyId} from session ${sessionId}`);
    // Since there's no explicit delete endpoint, we'll use the update endpoint with a 'removed' status
    const response = await axiosInstance.put(`/sessions/${sessionId}/competency/${competencyId}`, {
      status: 'removed'
    });
    console.log(`Removed session response:`, response.data);
    return response.data;
  },

  getCompetencyStatus: async (sessionId, competencyId) => {
    console.log(`Getting status for competency ${competencyId} in session ${sessionId}`);
    const response = await axiosInstance.get(`/sessions/${sessionId}/competency/${competencyId}`);
    console.log(`Competency status:`, response.data);
    return response.data;
  }
};

// Hook to fetch detailed session responses
export const useSessionResponses = (sessionId) => {
  return useQuery({
    queryKey: ['sessionDetailedResponses', sessionId],
    queryFn: () => sessionId ? sessionResponsesApi.getSessionResponses(sessionId) : Promise.resolve([]),
    enabled: !!sessionId,
  });
};

// Hook to fetch session competencies
export const useSessionCompetenciesIDs = (sessionId) => {
  const [selectedCompetencies, setSelectedCompetencies] = useState([]);
  
  // Fetch selected competencies from the backend
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['sessionResponses', sessionId],
    queryFn: () => sessionId ? sessionResponsesApi.getSessionCompetenciesIDs(sessionId) : Promise.resolve([]),
    enabled: !!sessionId,
    staleTime: 0, // Always refetch when requested
    cacheTime: 0, // Don't cache the results
  });

  // Update local state when data changes
  useEffect(() => {
    if (data) {
      console.log(`Setting selected competencies for session ${sessionId}:`, data);
      setSelectedCompetencies(data);
    }
  }, [data, sessionId]);

  return {
    selectedCompetencies,
    isLoading,
    error,
    refetch
  };
};

// Hook to save session competencies in bulk
export const useBulkSaveSessionResponses = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ sessionId, competencyIds }) => 
      sessionResponsesApi.bulkSaveSessionResponses({ sessionId, competencyIds }),
    onSuccess: (_, variables) => {
      console.log(`Successfully saved competencies for session ${variables.sessionId}`);
      // Invalidate and refetch the queries to ensure we have the latest data
      queryClient.invalidateQueries({ queryKey: ['sessionResponses', variables.sessionId] });
      queryClient.invalidateQueries({ queryKey: ['sessionDetailedResponses', variables.sessionId] });
      // Force a refetch
      queryClient.refetchQueries({ queryKey: ['sessionResponses', variables.sessionId] });
      queryClient.refetchQueries({ queryKey: ['sessionDetailedResponses', variables.sessionId] });
    },
    onError: (error, variables) => {
      console.error(`Error saving competencies for session ${variables.sessionId}:`, error);
    }
  });
}; 